import { apiFetch } from '@/stores/authStore';

export type InstitutionalDiscoveryRequest = {
  environment: 'preprod' | 'prod'; clientId: string; participantFirm: string;
  privateKey: string; participantId?: string;
};
export type InstitutionalDiscovery = {
  identity: { user: string; firm: string; audience: string };
  users: string[] | null; accounts: string[] | null; checkedAt: string;
};
export type DiscoverInstitutionalConnection = (
  request: InstitutionalDiscoveryRequest, signal: AbortSignal,
) => Promise<InstitutionalDiscovery>;

const errors = {
  invalid: 'The connection check returned an invalid response. Try again.',
  incomplete: 'The connection check did not complete. Try again.',
  session: 'Your session has expired. Sign in again.',
  access: 'You do not have access to check this connection.',
  busy: 'Connection checks are busy. Try again shortly.',
  configuration: 'Connection checking is not configured on this site.',
  provider: 'Could not complete the provider connection check. Try again later.',
  key: 'Choose a valid RSA private key in PEM format.',
  refused: 'Could not check the connection. Review its details and try again.',
} as const;
export class InstitutionalDiscoveryError extends Error {
  constructor(reason: keyof typeof errors) { super(errors[reason]); }
}

const record = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
const name = (value: unknown): value is string => typeof value === 'string'
  && value.length > 0 && value.length <= 2048 && !/[\s\x00-\x1f\x7f]/.test(value);

/** Discovery is evidence for a later disabled save, never account activation.
 * Keep user and account lists independent; array position is not a mapping. */
export function decodeInstitutionalDiscovery(raw: unknown): InstitutionalDiscovery {
  const d = record(record(raw)?.discovery), identity = record(d?.identity);
  const roster = (value: unknown): value is string[] | null => value === null
    || (Array.isArray(value) && value.length <= 1024 && value.every(name) && new Set(value).size === value.length);
  if (!d || !identity || !name(identity.user) || !name(identity.firm) || !name(identity.audience)
    || !roster(d.users) || !roster(d.accounts) || (d.users === null) !== (d.accounts === null)
    || typeof d.checkedAt !== 'string' || !/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d\.\d{3}Z$/.test(d.checkedAt)
    || !Number.isFinite(Date.parse(d.checkedAt))) throw new InstitutionalDiscoveryError('invalid');
  return { identity: { user: identity.user, firm: identity.firm, audience: identity.audience },
    users: d.users, accounts: d.accounts, checkedAt: d.checkedAt };
}

export async function discoverInstitutionalConnection(request: InstitutionalDiscoveryRequest,
  signal: AbortSignal, orgQueryParam = ''): Promise<InstitutionalDiscovery> {
  let response: Response;
  try {
    response = await apiFetch(`/api/manager/polymarket-us/institutional-connections/discover${orgQueryParam}`, {
      method: 'POST', signal, cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request),
    });
  } catch {
    throw new InstitutionalDiscoveryError('incomplete');
  }
  if (!response.ok) {
    // Do not echo provider bodies: they can include submitted connection values.
    const code = record(await response.json().catch(() => null))?.code;
    const reason = response.status === 401 ? 'session' : response.status === 403 ? 'access'
      : code === 'US_CREDENTIAL_CHECK_BUSY' || response.status === 429 ? 'busy'
        : code === 'US_INSTITUTIONAL_COORDINATION_REQUIRED' ? 'configuration'
          : code === 'US_INSTITUTIONAL_DISCOVERY_UNAVAILABLE' ? 'provider'
            : code === 'US_INSTITUTIONAL_KEY_INVALID' ? 'key' : 'refused';
    throw new InstitutionalDiscoveryError(reason);
  }
  return decodeInstitutionalDiscovery(await response.json().catch(() => null));
}
